// COSTA BLANCA WORLDS — cloned/adapted from Sakura Crossing
// codex/foundation-05-experience-navigation src/studio/mediaStore.js
// Capability preserved: IndexedDB blob store + runtime object URL lifecycle.

export const MEDIA_DB_NAME = 'costa-blanca-world-media-assets-v1';
export const MEDIA_STORE_NAME = 'assets';

const IMAGE_MIME = new Set(['image/png', 'image/jpeg', 'image/webp']);
const VIDEO_MIME = new Set(['video/mp4', 'video/webm']);
const runtimeUrls = new Map();

function kindFromExtension(name = '') {
  const ext = String(name).toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] || '';
  if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) return 'image';
  if (['mp4', 'm4v', 'webm'].includes(ext)) return 'video';
  return null;
}

export function classifyMedia(file) {
  if (IMAGE_MIME.has(file?.type)) return 'image';
  if (VIDEO_MIME.has(file?.type)) return 'video';
  // Same rule already proven by Property Room Studio: Windows frequently sends
  // MP4/WebM with file.type === ''. The extension is therefore a supported
  // fallback, not a relaxation of the media contract.
  const byExtension = kindFromExtension(file?.name);
  if (byExtension) return byExtension;
  throw new Error('Unsupported media. Use PNG, JPEG, WebP, MP4 or WebM.');
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) return reject(new Error('IndexedDB is unavailable.'));
    const request = indexedDB.open(MEDIA_DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(MEDIA_STORE_NAME)) {
        request.result.createObjectStore(MEDIA_STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed.'));
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(MEDIA_STORE_NAME, mode);
      const store = tx.objectStore(MEDIA_STORE_NAME);
      const result = fn(store);
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed.'));
    });
  } finally { db.close(); }
}

function withoutBlob(record) {
  const { blob, ...meta } = record;
  return meta;
}

export async function putMediaAsset(file) {
  const kind = classifyMedia(file);
  const id = `asset-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const record = { id, name: file.name || id, type: kind, mime: file.type || '', size: file.size || 0, updatedAt: new Date().toISOString(), blob: file };
  await withStore('readwrite', (store) => store.put(record));
  revokeMediaUrl(id);
  return withoutBlob(record);
}

export async function getMediaAsset(id) {
  if (!id) return null;
  return withStore('readonly', (store) => {
    const request = store.get(id);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error ?? new Error('Asset read failed.'));
    });
  });
}

export async function deleteMediaAsset(id) {
  if (!id) return;
  revokeMediaUrl(id);
  await withStore('readwrite', (store) => store.delete(id));
}

export async function resolveMediaAsset(id) {
  if (!id) return null;
  const cached = runtimeUrls.get(id);
  if (cached) return cached;
  const record = await getMediaAsset(id);
  if (!record?.blob) return null;
  const url = URL.createObjectURL(record.blob);
  const resolved = { ...withoutBlob(record), url };
  runtimeUrls.set(id, resolved);
  return resolved;
}

export function revokeMediaUrl(id) {
  const current = runtimeUrls.get(id);
  if (current?.url) URL.revokeObjectURL(current.url);
  runtimeUrls.delete(id);
}

export function revokeAllMediaUrls() {
  for (const id of runtimeUrls.keys()) revokeMediaUrl(id);
}
